import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import {
    Dismiss12Regular,
    Warning12Filled,
    CheckmarkCircle12Filled,
    ChevronRight12Regular,
} from '@fluentui/react-icons';
import { DrawerBody, OverlayDrawer } from '@fluentui/react-components';
import { buildSourceReadinessNavigation } from '../../../utils/intelligenceSourceReadiness';
import styles from './intelligenceInsightsOverview.module.css';

const mountNode = typeof document !== 'undefined' ? document.body : undefined;

const EMPTY_SOURCE_READINESS = {
    summary: { ready: 0, processing: 0, failed: 0 },
    tabs: ['All', 'Ready', 'Processing', 'Failed', 'Material impact'],
    sources: [],
};

function versionStatusDotClass(status, styles) {
    if (status === 'current') return styles.versionStatusDotCurrent;
    if (status === 'generating') return styles.versionStatusDotGenerating;
    return styles.versionStatusDotMuted;
}

export function VersionHistoryDrawer({ open, onClose, versions = [], onOpenVersion }) {
    return (
        <OverlayDrawer
            open={open}
            position="end"
            modalType="modal"
            mountNode={mountNode}
            style={{ width: 400, maxWidth: '100vw' }}
            onOpenChange={(_, d) => {
                if (!d.open) onClose?.();
            }}>
            <DrawerBody className={styles.ovDrawerBody}>
                <div className={styles.ovDrawerHeader}>
                    <div>
                        <h2 className={styles.ovDrawerTitle}>Version History</h2>
                    </div>
                    <div className={styles.ovDrawerHeaderActions}>
                        <button type="button" className={styles.btnIcon} onClick={onClose} aria-label="Close">
                            <Dismiss12Regular />
                        </button>
                    </div>
                </div>
                <div className={styles.ovDrawerContent}>
                    {!versions.length ? (
                        <p className={styles.versionMeta}>No analysis versions yet.</p>
                    ) : null}
                    {versions.map(v => {
                        const statusLabel = v.status_label || v.statusLabel || v.status;
                        return (
                            <article key={v.id} className={styles.versionCard}>
                                <div className={styles.versionTitleRow}>
                                    <p className={styles.versionLabel}>{v.label}</p>
                                    <span
                                        className={`${styles.versionStatusDot} ${versionStatusDotClass(v.status, styles)}`}
                                        aria-hidden
                                        title={statusLabel}
                                    />
                                </div>
                                <p className={styles.versionMeta}>{v.meta}</p>
                                {v.rag ? <p className={styles.versionMeta}>{v.rag}</p> : null}
                                {v.confidence ? (
                                    <p className={styles.versionMeta}>{v.confidence}</p>
                                ) : null}
                                <div className={styles.versionActions}>
                                    {(v.actions || [])
                                        .filter(label => label === 'Open')
                                        .map(label => (
                                        <button
                                            key={label}
                                            type="button"
                                            className={
                                                label === 'Open'
                                                    ? styles.versionBtnOpen
                                                    : styles.linkMuted
                                            }
                                            onClick={() => onOpenVersion?.(v)}>
                                            {label}
                                        </button>
                                    ))}
                                </div>
                            </article>
                        );
                    })}
                </div>
            </DrawerBody>
        </OverlayDrawer>
    );
}

export function AnalysisDetailsDrawer({ open, onClose, onOpenVersions, onOpenSourceSnapshot, rows = [] }) {
    return (
        <OverlayDrawer
            open={open}
            position="end"
            modalType="modal"
            mountNode={mountNode}
            style={{ width: 400, maxWidth: '100vw' }}
            onOpenChange={(_, d) => {
                if (!d.open) onClose?.();
            }}>
            <DrawerBody className={styles.ovDrawerBody}>
                <div className={styles.ovDrawerHeader}>
                    <h2 className={styles.ovDrawerTitle}>Analysis Details</h2>
                    <button type="button" className={styles.btnIcon} onClick={onClose} aria-label="Close">
                        <Dismiss12Regular />
                    </button>
                </div>
                <div className={styles.ovDrawerContent}>
                    <dl className={styles.detailsList}>
                        {rows.map(row => (
                            <div key={row.label} className={styles.detailsRow}>
                                <dt>{row.label}</dt>
                                <dd>
                                    {row.tone === 'warning' ? (
                                        <span className={styles.detailsWarning}>
                                            <Warning12Filled aria-hidden /> {row.value}
                                        </span>
                                    ) : (
                                        row.value
                                    )}
                                </dd>
                            </div>
                        ))}
                    </dl>
                    <div className={styles.detailsFooter}>
                        {onOpenSourceSnapshot ? (
                            <button
                                type="button"
                                className={styles.linkPrimary}
                                onClick={() => onOpenSourceSnapshot?.()}>
                                View source snapshot
                            </button>
                        ) : null}
                        <button type="button" className={styles.linkPrimary} onClick={onOpenVersions}>
                            View versions
                        </button>
                    </div>
                </div>
            </DrawerBody>
        </OverlayDrawer>
    );
}

function snapshotStatusClass(status, styles) {
    if (status === 'failed') return styles.snapshotChipFailed;
    if (status === 'processing') return styles.snapshotChipProcessing;
    return styles.snapshotChipReady;
}

function SnapshotSourceRow({ row, onOpen }) {
    if (!row) return null;
    return (
        <article className={styles.snapshotSourceRow}>
            <div className={styles.snapshotSourceMain}>
                <p className={styles.snapshotSourceTitle}>{row.title}</p>
                <p className={styles.snapshotSourceMeta}>{row.meta}</p>
                {row.impact ? <p className={styles.snapshotSourceImpact}>{row.impact}</p> : null}
            </div>
            <div className={styles.snapshotSourceActions}>
                <span
                    className={`${styles.snapshotStatusChip} ${snapshotStatusClass(row.status, styles)}`}>
                    {row.statusLabel || row.status}
                </span>
                <button type="button" className={styles.snapshotOpenLink} onClick={() => onOpen?.(row)}>
                    Open
                    <ChevronRight12Regular aria-hidden />
                </button>
            </div>
        </article>
    );
}

export function SourceSnapshotDrawer({ open, onClose, data }) {
    const navigate = useNavigate();
    if (!data) return null;

    const handleOpenSource = row => {
        const target = buildSourceReadinessNavigation(row);
        if (!target?.path) return;
        onClose?.();
        navigate(target.path, target.state ? { state: target.state } : undefined);
    };

    const summary = data.summary || {};
    const summaryTiles = [
        { value: summary.included, label: 'Included' },
        { value: summary.excluded, label: 'Excluded' },
        { value: summary.vdr, label: 'VDR files' },
        { value: summary.irl, label: 'IRL responses' },
        { value: summary.observations, label: 'Observations' },
        { value: summary.failed, label: 'Failed' },
    ];

    const includedGroups = (data.includedGroups || []).filter(g => g.count > 0);
    const excludedGroups = data.excludedGroups || [];

    return (
        <OverlayDrawer
            open={open}
            position="end"
            modalType="modal"
            mountNode={mountNode}
            style={{ width: 520, maxWidth: '100vw' }}
            onOpenChange={(_, d) => {
                if (!d.open) onClose?.();
            }}>
            <DrawerBody className={styles.ovDrawerBody}>
                <div className={styles.ovDrawerHeader}>
                    <h2 className={styles.ovDrawerTitle}>Source Snapshot</h2>
                    <button type="button" className={styles.btnIcon} onClick={onClose} aria-label="Close">
                        <Dismiss12Regular />
                    </button>
                </div>
                <div className={styles.snapshotDrawerContent}>
                    <section className={styles.snapshotMetaCard}>
                        {data.header?.titleLine ? (
                            <p className={styles.snapshotMetaTitle}>{data.header.titleLine}</p>
                        ) : null}
                        {data.header?.generatedLabel ? (
                            <p className={styles.snapshotMetaSub}>
                                {data.header.generatedLabel}
                            </p>
                        ) : null}
                    </section>

                    <p className={styles.snapshotSectionLabel}>Summary</p>
                    <section className={styles.snapshotSummaryGrid}>
                        {summaryTiles.map(tile => (
                            <div key={tile.label} className={styles.snapshotSummaryTile}>
                                <p className={styles.snapshotSummaryValue}>{tile.value ?? 0}</p>
                                <p className={styles.snapshotSummaryLabel}>{tile.label}</p>
                            </div>
                        ))}
                    </section>

                    {includedGroups.length ? (
                        <>
                            <p className={styles.snapshotSectionLabel}>Included sources</p>
                            {includedGroups.map(group => (
                                <section key={group.key} className={styles.snapshotGroup}>
                                    <div className={styles.snapshotGroupHeader}>
                                        <p className={styles.snapshotGroupTitle}>{group.label}</p>
                                        <p className={styles.snapshotGroupCount}>{group.countLabel}</p>
                                    </div>
                                    {(group.preview || []).map(row => (
                                        <SnapshotSourceRow
                                            key={`${group.key}-${row.id}`}
                                            row={row}
                                            onOpen={handleOpenSource}
                                        />
                                    ))}
                                    {group.moreLabel ? (
                                        <p className={styles.snapshotMoreLabel}>{group.moreLabel}</p>
                                    ) : null}
                                </section>
                            ))}
                        </>
                    ) : null}

                    {excludedGroups.length ? (
                        <>
                            <p className={styles.snapshotSectionLabel}>Excluded sources</p>
                            {excludedGroups.map(group => (
                                <section key={group.key} className={styles.snapshotGroup}>
                                    <p className={styles.snapshotGroupTitle}>{group.label}</p>
                                    {(group.items || []).map(row => (
                                        <SnapshotSourceRow
                                            key={`${group.key}-${row.id}`}
                                            row={row}
                                            onOpen={handleOpenSource}
                                        />
                                    ))}
                                </section>
                            ))}
                        </>
                    ) : null}

                    {data.catalogTruncated ? (
                        <p className={styles.snapshotTruncatedNote}>
                            Source catalog was truncated for display. Totals reflect the full snapshot.
                        </p>
                    ) : null}

                    {data.footerNote ? (
                        <p className={styles.snapshotFooterNote}>{data.footerNote}</p>
                    ) : null}
                </div>
            </DrawerBody>
        </OverlayDrawer>
    );
}

export function SourceReadinessDrawer({
    open,
    onClose,
    data,
    initialTab = null,
    highlightSourceId = null,
}) {
    const navigate = useNavigate();
    const readiness = data || EMPTY_SOURCE_READINESS;
    const [tab, setTab] = useState('All');
    const cardRefs = useRef({});
    const filtered = (readiness.sources || []).filter(s => {
        if (tab === 'All') return true;
        if (tab === 'Ready') return s.status === 'ready';
        if (tab === 'Processing') return s.status === 'processing';
        if (tab === 'Failed') return s.status === 'failed';
        if (tab === 'Material impact') return Boolean(s.impact);
        return true;
    });

    useEffect(() => {
        if (!open) {
            setTab('All');
            return;
        }
        if (initialTab) setTab(initialTab);
    }, [open, initialTab]);

    const resolvedHighlightId = useMemo(() => {
        if (highlightSourceId) return highlightSourceId;
        if (tab === 'Failed' && filtered.length) {
            const firstFailed = filtered.find(s => s.status === 'failed');
            return firstFailed?.id || filtered[0]?.id || null;
        }
        return null;
    }, [highlightSourceId, tab, filtered]);

    useEffect(() => {
        if (!open || !resolvedHighlightId) return undefined;
        const frame = requestAnimationFrame(() => {
            const el = cardRefs.current[resolvedHighlightId];
            el?.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' });
        });
        return () => cancelAnimationFrame(frame);
    }, [open, resolvedHighlightId, tab, filtered.length]);

    const handleSourceClick = source => {
        const target = buildSourceReadinessNavigation(source);
        if (!target?.path) return;
        onClose?.();
        navigate(target.path, target.state ? { state: target.state } : undefined);
    };

    return (
        <OverlayDrawer
            open={open}
            position="end"
            modalType="modal"
            mountNode={mountNode}
            style={{ width: 420, maxWidth: '100vw' }}
            onOpenChange={(_, d) => {
                if (!d.open) onClose?.();
            }}>
            <DrawerBody className={styles.ovDrawerBody}>
                <div className={styles.ovDrawerHeader}>
                    <div>
                        <h2 className={styles.ovDrawerTitle}>Source readiness</h2>
                        <p className={styles.sourceSummary}>
                            <span className={styles.sourceReady}>
                                {readiness.summary?.ready ?? 0} Ready
                            </span>
                            {' · '}
                            <span className={styles.sourceProcessing}>
                                {readiness.summary?.processing ?? 0} Processing
                            </span>
                            {' · '}
                            <span className={styles.sourceFailed}>
                                {readiness.summary?.failed ?? 0} Failed
                            </span>
                        </p>
                    </div>
                    <button type="button" className={styles.btnIcon} onClick={onClose} aria-label="Close">
                        <Dismiss12Regular />
                    </button>
                </div>
                <div className={styles.sourceTabs}>
                    {(readiness.tabs || EMPTY_SOURCE_READINESS.tabs).map(t => (
                        <button
                            key={t}
                            type="button"
                            className={`${styles.sourceTab} ${tab === t ? styles.sourceTabActive : ''}`}
                            onClick={() => setTab(t)}>
                            {t}
                        </button>
                    ))}
                </div>
                <div className={styles.ovDrawerContent}>
                    {!filtered.length ? (
                        <p className={styles.versionMeta}>No sources in this filter.</p>
                    ) : null}
                    {filtered.map(src => (
                        <article
                            key={src.id}
                            ref={el => {
                                if (el) cardRefs.current[src.id] = el;
                            }}
                            className={`${styles.sourceCard} ${styles.sourceCardInteractive} ${
                                src.status === 'failed' ? styles.sourceCardFailed : ''
                            } ${resolvedHighlightId === src.id ? styles.sourceCardFocused : ''}`}
                            role="button"
                            tabIndex={0}
                            aria-label={`Open ${src.title}`}
                            onClick={() => handleSourceClick(src)}
                            onKeyDown={e => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    handleSourceClick(src);
                                }
                            }}>
                            <div className={styles.sourceCardTop}>
                                <div>
                                    <p className={styles.sourceTitle}>{src.title}</p>
                                    <p className={styles.sourceMeta}>{src.meta}</p>
                                </div>
                                <span
                                    className={`${styles.sourceStatus} ${
                                        src.status === 'failed'
                                            ? styles.sourceStatusFailed
                                            : src.status === 'processing'
                                              ? styles.sourceStatusProcessing
                                              : styles.sourceStatusReady
                                    }`}>
                                    {src.statusLabel}
                                </span>
                            </div>
                            {src.impact ? <p className={styles.sourceImpact}>{src.impact}</p> : null}
                        </article>
                    ))}
                </div>
            </DrawerBody>
        </OverlayDrawer>
    );
}

function ModalShell({ open, title, subtitle, onClose, footer, children, wide }) {
    useEffect(() => {
        if (!open) return undefined;
        const onKey = e => {
            if (e.key === 'Escape') onClose?.();
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [open, onClose]);

    if (!open || !mountNode) return null;

    return createPortal(
        <div className={styles.modalBackdrop} role="presentation" onClick={onClose}>
            <div
                className={`${styles.modalCard} ${wide ? styles.modalCardWide : ''}`}
                role="dialog"
                aria-modal="true"
                aria-label={title}
                onClick={e => e.stopPropagation()}>
                <div className={styles.modalHeader}>
                    <div>
                        <h2 className={styles.modalTitle}>{title}</h2>
                        {subtitle ? <p className={styles.modalSubtitle}>{subtitle}</p> : null}
                    </div>
                    <button type="button" className={styles.btnIcon} onClick={onClose} aria-label="Close">
                        <Dismiss12Regular />
                    </button>
                </div>
                <div className={styles.modalBody}>{children}</div>
                {footer ? <div className={styles.modalFooter}>{footer}</div> : null}
            </div>
        </div>,
        mountNode,
    );
}

export function ModuleConfigModal({ open, onClose, modules = [] }) {
    const [rows, setRows] = useState(() => modules.map(m => ({ ...m })));

    useEffect(() => {
        if (open) {
            setRows(modules.map(m => ({ ...m })));
        }
    }, [open, modules]);

    return (
        <ModalShell
            open={open}
            title="Module configuration"
            subtitle="Only visible to Project Admin users."
            onClose={onClose}
            wide
            footer={
                <>
                    <p className={styles.modalFooterNote}>
                        Disabled modules are not counted as failed and remain in version history.
                    </p>
                    <div className={styles.modalFooterActions}>
                        <button type="button" className={styles.btnSecondary} onClick={onClose}>
                            Close
                        </button>
                    </div>
                </>
            }>
            <div className={styles.configList}>
                {rows.map(row => (
                    <div key={row.key} className={styles.configRow}>
                        <div>
                            <p className={styles.configTitle}>{row.title}</p>
                            <p className={styles.configDesc}>{row.description}</p>
                            <p className={styles.configType}>Project type: {row.projectType}</p>
                        </div>
                        <div className={styles.segmented}>
                            <button
                                type="button"
                                className={`${styles.segment} ${row.enabled ? styles.segmentActive : ''}`}
                                onClick={() =>
                                    setRows(prev =>
                                        prev.map(r =>
                                            r.key === row.key ? { ...r, enabled: true } : r,
                                        ),
                                    )
                                }>
                                Enabled
                            </button>
                            <button
                                type="button"
                                className={`${styles.segment} ${!row.enabled ? styles.segmentActive : ''}`}
                                onClick={() =>
                                    setRows(prev =>
                                        prev.map(r =>
                                            r.key === row.key ? { ...r, enabled: false } : r,
                                        ),
                                    )
                                }>
                                Not applicable
                            </button>
                        </div>
                    </div>
                ))}
            </div>
        </ModalShell>
    );
}

export function GenerateVersionModal({ open, onClose, onConfirm, generating, data }) {
    const modal = data || {
        title: 'Generate analysis version?',
        rows: [],
        note: 'This will create a new immutable analysis version and regenerate all insight modules.',
        confirmLabel: 'Generate →',
    };
    return (
        <ModalShell
            open={open}
            title={modal.title}
            onClose={onClose}
            footer={
                <div className={styles.modalFooterActions}>
                    <button type="button" className={styles.btnSecondary} onClick={onClose}>
                        Cancel
                    </button>
                    <button
                        type="button"
                        className={styles.btnPrimary}
                        disabled={generating}
                        onClick={onConfirm}>
                        {generating ? 'Generating…' : modal.confirmLabel}
                    </button>
                </div>
            }>
            <dl className={styles.generateModalRows}>
                {(modal.rows || []).map(row => (
                    <div key={row.label} className={styles.generateModalRow}>
                        <dt>{row.label}</dt>
                        <dd>{row.value}</dd>
                    </div>
                ))}
            </dl>
            <p className={styles.generateModalNote}>{modal.note}</p>
        </ModalShell>
    );
}

export function OverviewActionMenu({ open, onClose, onSelect, anchorRef, items = [] }) {
    useEffect(() => {
        if (!open) return undefined;
        const onDoc = e => {
            if (anchorRef?.current?.contains?.(e.target)) return;
            onClose?.();
        };
        const onKey = e => {
            if (e.key === 'Escape') onClose?.();
        };
        document.addEventListener('mousedown', onDoc);
        window.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onDoc);
            window.removeEventListener('keydown', onKey);
        };
    }, [open, onClose, anchorRef]);

    if (!open || !items.length) return null;

    return (
        <div className={styles.actionMenu} role="menu">
            {items.map(item =>
                item.key === 'divider' ? (
                    <div key="divider" className={styles.actionMenuDivider} />
                ) : (
                    <button
                        key={item.key}
                        type="button"
                        role="menuitem"
                        aria-disabled={item.disabled || undefined}
                        className={`${styles.actionMenuItem} ${
                            item.disabled ? styles.actionMenuItemDisabled : ''
                        }`}
                        onClick={() => {
                            if (item.disabled) return;
                            onSelect?.(item.key);
                            onClose?.();
                        }}>
                        {item.label}
                    </button>
                ),
            )}
        </div>
    );
}

export function CompleteIcon() {
    return <CheckmarkCircle12Filled className={styles.statusIcon} aria-hidden />;
}
